import { createElement } from 'react';

import { clone, isEqual } from 'lodash';

import defaultUserImage from '../assets/images/default_user.png';
import defaultSkillImage from '../assets/images/gray-gradient.png';
import LearningObjectiveData from '../types/learningObjective';

export interface Orderable {
  order: number;
}

export interface OrderableWithId extends Orderable {
  id: string;
}

export function swapOrder<T extends Orderable>(items: T[], item1: T, item2: T): T[] {
  const order1 = item1.order;
  const order2 = item2.order;

  return (
    items
      .map(item => {
        if (item === item1) {
          const newItem = clone(item);
          newItem.order = order2;
          return newItem;
        }
        if (item === item2) {
          const newItem = clone(item);
          newItem.order = order1;
          return newItem;
        }
        return item;
      })
      .sort((a, b) => a.order - b.order)
      // Assign order by index in case order integrity has been violated.
      .map((item, index) => {
        if (item.order === index + 1) return item;
        const newItem = clone(item);
        newItem.order = index + 1;
        return newItem;
      })
  );
}

/**
 * Inserts an object into an array at index. Returns a new array and
 * leaves the input array unmodified.
 * @param array
 * @param index
 * @param value
 */
export function insert<T>(array: T[], index: number, value: T) {
  const newArray = [...array];
  newArray.splice(index, 0, value);
  return newArray;
}

/**
 * Inserts an orderable object into an array at index given by order - 1.
 * Returns a new array where all elements have had their order updated after insertion.
 * Leaves the input array unmodified.
 * @param array
 * @param value
 */
export function insertInOrder<T extends Orderable>(array: T[], value: T): T[] {
  const arrayAfterInsertion = insert(array, value.order - 1, value);
  return arrayAfterInsertion.map((value, i) => ({ ...value, order: i + 1 }));
}

/**
 * Removes an orderable element with an id from an array. Returns a new array
 * where all elements have had their order updated after deletion.
 * Leaves the input array unmodified.
 * @param array
 * @param targetElement
 */
export function removeOrderableWithId<T extends OrderableWithId>(targetElement: T, array: T[]): T[] {
  return array.filter(element => element.id !== targetElement.id).map((element, i) => ({ ...element, order: i + 1 }));
}

export function swapLocations(sections: any[], index1: number, index2: number) {
  [sections[index1], sections[index2]] = [sections[index2], sections[index1]];
}

/**
 * Helper function to statically make sure that switch statements are exhaustive.
 * See https://stackoverflow.com/a/39419171/12794481
 * @param x
 */
export function assertUnreachable(x: never): never {
  throw new Error("Didn't expect to get here");
}

export const firestoreAutoId = (): string => {
  const CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

  let autoId = '';

  for (let i = 0; i < 20; i++) {
    autoId += CHARS.charAt(Math.floor(Math.random() * CHARS.length));
  }
  return autoId;
};

/**
 * Generates a random bright color.
 */
export function randomHsl() {
  return 'hsla(' + Math.random() * 360 + ', 100%, 50%, 1)';
}

export function getLoColorById(los: LearningObjectiveData[], loId: string) {
  const lo = los.find(lo => lo.id === loId);
  return lo?.color;
}

type SortableType = string | number;

/**
 * Returns true if the two given arrays have exactly the same elements via equality comparison.
 * Order of elements is not taken into consideration.
 * @param arr1
 * @param arr2
 */
export function arraysHaveSameValues<T extends SortableType>(arr1: T[], arr2: T[]) {
  return isEqual(arr1.sort(), arr2.sort());
}

export function getOrLogError<T>(obj: T | null | undefined, message = ''): T {
  if (typeof obj === 'boolean') {
    return obj;
  }

  if (typeof obj === 'number' || typeof obj === 'bigint') {
    return obj;
  }

  if (typeof obj === 'string') {
    return obj;
  }

  console.assert(obj, message);
  return obj!;
}

/**
 * Returns the filename extension from a filename. Returns empty string if no extension is found.
 */
export function getFilenameExtension(filename: string): string {
  return (filename.match(/\.[0-9a-z]{1,5}$/i) || [''])[0].substring(1);
}

/**
 * Returns an array of paragraph react elements with each paragraph corresponding to each line separated by \n in text.
 * @param text
 */
export function textToParagraphs(text?: string) {
  if (!text) {
    return undefined;
  }
  return text.split('\n').map((line, i) => createElement('p', { key: i }, line));
}

export function getDefaultUserImageUrl() {
  return defaultUserImage;
}

export function getDefaultSkillImageUrl() {
  return defaultSkillImage;
}

/**
 * Adds value to array if it does not already exist. If it exists, remove from array.
 * Result is returned as a new array, original array is unaffected.
 */
export function xorArray<T>(array: T[], value: T) {
  const resultingArray = [...array];
  if (!resultingArray.includes(value)) {
    resultingArray.push(value);
  } else {
    resultingArray.splice(resultingArray.indexOf(value), 1); //deleting
  }
  return resultingArray;
}

export function bgFade(elementId: string, opacity: number = 1) {
  const comment = document.getElementById(elementId);
  if (!comment) return;
  const newOpacity = opacity - 0.02;
  comment.style.backgroundColor = 'rgba(255, 242, 130, ' + Math.max(newOpacity, 0) + ')';
  if (newOpacity > 0) {
    setTimeout(() => bgFade(elementId, newOpacity), 30);
  }
}

export function getEnumKeyByEnumValue<T extends { [index: string]: string }>(
  myEnum: T,
  enumValue: string
): keyof T | null {
  let keys = Object.keys(myEnum).filter(x => myEnum[x] === enumValue);
  return keys.length > 0 ? keys[0] : null;
}

export function delay(ms: number) {
  return new Promise(res => setTimeout(res, ms));
}
